import { Form, Input, Modal, Upload } from 'antd';
import { useCallback, useMemo, useState, type FC } from 'react';

import type {
  PortfolioAddFormValues,
  PortfolioAddModalProps,
} from '@pages/Portfolio/types';

import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const { useForm, Item } = Form;
const { TextArea } = Input;
const { Dragger } = Upload;

export const PortfolioAddModal: FC<PortfolioAddModalProps> = ({
  open,
  confirmLoading,
  onCancel,
  onSubmit,
  disabled,
}) => {
  const [form] = useForm<PortfolioAddFormValues>();
  const { resetFields, submit } = form;
  const [fileList, setFileList] = useState<UploadFile<File>[]>([]);

  const uploadProps: UploadProps<File> = useMemo(
    () => ({
      name: 'file',
      multiple: true,
      accept: 'image/*',
      fileList,
      beforeUpload: () => false,
      onChange: (info: UploadChangeParam<UploadFile<File>>) => {
        setFileList(info.fileList);
      },
    }),
    [fileList]
  );

  const handleOk = useCallback(() => {
    submit();
  }, [submit]);

  const handleFinish = useCallback(
    async (values: PortfolioAddFormValues) => {
      const files = fileList
        .map((item) => item.originFileObj)
        .filter((file) => Boolean(file)) as File[];

      await onSubmit({
        description: values.description ?? null,
        files,
      });
    },
    [fileList, onSubmit]
  );

  const afterClose = useCallback(() => {
    resetFields();
    setFileList([]);
  }, [resetFields, setFileList]);

  return (
    <Modal
      open={open}
      title="Добавление изображений"
      okText="Сохранить"
      cancelText="Отмена"
      onOk={handleOk}
      onCancel={onCancel}
      afterClose={afterClose}
      confirmLoading={confirmLoading}
      destroyOnHidden
      className="bc-employee-portfolio-modal"
      okButtonProps={{ disabled }}
      cancelButtonProps={{ disabled }}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Item
          label="Файлы"
          required
          className="bc-employee-portfolio-modal__upload"
        >
          <Dragger {...uploadProps}>
            <p className="bc-employee-portfolio-modal__upload-text">
              Перетащите файлы сюда или нажмите, чтобы выбрать
            </p>
          </Dragger>
        </Item>
        <Item
          label="Описание"
          name="description"
          className="bc-employee-portfolio-modal__description"
        >
          <TextArea rows={4} maxLength={4000} showCount />
        </Item>
      </Form>
    </Modal>
  );
};
